import { z } from "zod/v3";

/**
 * Layout options for sections (primarily skills, custom)
 */
export const sectionLayoutSchema = z.enum(["default", "grouped", "inline", "grid"]);
export type SectionLayout = z.infer<typeof sectionLayoutSchema>;

/**
 * Style options for sections (interests, languages, profiles)
 */
export const sectionStyleSchema = z.enum(["list", "tags", "comma"]);
export type SectionStyle = z.infer<typeof sectionStyleSchema>;

/**
 * Display settings for section-specific customization
 * All fields are optional - undefined means "use template default"
 */
export const displaySettingsSchema = z
  .object({
    /** Layout mode (for skills, custom sections) */
    layout: sectionLayoutSchema.optional(),
    /** Style mode (for interests, languages, profiles) */
    style: sectionStyleSchema.optional(),
    /** Show skill/language proficiency levels */
    showLevel: z.boolean().optional(),
    /** Show date fields */
    showDate: z.boolean().optional(),
    /** Show location fields */
    showLocation: z.boolean().optional(),
    /** Show description/summary fields */
    showDescription: z.boolean().optional(),
    /** Compact display mode */
    compact: z.boolean().optional(),
  })
  .optional();

export type DisplaySettings = z.infer<typeof displaySettingsSchema>;

/**
 * Resolved display settings with all values guaranteed (no undefined)
 * Used after merging defaults + template + user overrides
 */
export type ResolvedDisplaySettings = {
  layout: SectionLayout;
  style: SectionStyle;
  showLevel: boolean;
  showDate: boolean;
  showLocation: boolean;
  showDescription: boolean;
  compact: boolean;
};

/**
 * Default display settings used as fallback
 */
export const defaultDisplaySettings: ResolvedDisplaySettings = {
  layout: "default",
  style: "list",
  showLevel: false,
  showDate: true,
  showLocation: true,
  showDescription: true,
  compact: false,
};
